// Lightweight i18n — EN + AR with full RTL switching (CLAUDE.md rule 6).
// No hardcoded UI strings anywhere: components call useT() and reference keys.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import en from '../i18n/en.json';
import ar from '../i18n/ar.json';

export type Lang = 'en' | 'ar';

const catalogs: Record<Lang, Record<string, string>> = { en, ar };

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return key in params ? String(params[key]) : match;
  });
}

interface I18nValue {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  t: (key: string, params?: Record<string, string | number>) => string;
  setLang: (lang: Lang) => void;
}

const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = 'mqr.lang';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'ar' ? 'ar' : 'en';
  });

  const setLang = useCallback((next: Lang) => {
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
  }, []);

  const dir: 'ltr' | 'rtl' = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const raw = catalogs[lang][key] ?? catalogs.en[key] ?? key;
      return interpolate(raw, params);
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, dir, t, setLang }), [lang, dir, t, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

export function useT() {
  return useI18n().t;
}

/** Pure helper for tests / non-React code. */
export function translate(lang: Lang, key: string, params?: Record<string, string | number>): string {
  const raw = catalogs[lang][key] ?? catalogs.en[key] ?? key;
  return interpolate(raw, params);
}
